import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Navbar from "@/components/navbar";
import AdminHeader from "@/components/admin-header";
import { useToast } from "@/hooks/use-toast";
import { Music, Users, Heart, Search, RefreshCw, Save, Trash2, Plus } from "lucide-react";

interface DynamicValue {
  value: string;
  label: string;
  isActive: boolean;
  count?: number;
  normalized?: string;
}

interface ManagedValue {
  id: number;
  value: string;
  label: string;
  description?: string;
  category: string;
  isActive: boolean;
  sortOrder: number;
}

export default function DynamicValuesManagement() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("musicGenres");
  const [selectedValues, setSelectedValues] = useState<Set<string>>(new Set());
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch dynamic values
  const { data: dynamicValues = [], isLoading: loadingDynamic, refetch: refetchDynamic } = useQuery({
    queryKey: ['/api/filter-options', selectedCategory],
    queryFn: async () => {
      const response = await fetch(`/api/filter-options?category=${selectedCategory}`);
      if (!response.ok) throw new Error('Failed to fetch dynamic values');
      return response.json();
    },
  });

  // Fetch managed values for the category
  const { data: managedValues = [], isLoading: loadingManaged } = useQuery({
    queryKey: ['/api/filter-options', 'managed', selectedCategory],
    queryFn: async () => {
      const response = await fetch('/api/filter-options');
      if (!response.ok) throw new Error('Failed to fetch managed values');
      const all = await response.json();
      return all.filter((v: ManagedValue) => v.category === selectedCategory);
    },
  });

  // Create managed value from dynamic value
  const createManagedValue = useMutation({
    mutationFn: async (values: string[]) => {
      const promises = values.map(value => 
        fetch('/api/filter-options', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            value,
            label: value,
            category: selectedCategory,
            isActive: true,
          }),
        })
      );
      return Promise.all(promises);
    },
    onSuccess: () => {
      toast({
        title: "Values approved",
        description: "Selected values have been added to managed list",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/filter-options'] });
      setSelectedValues(new Set());
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to approve values",
        variant: "destructive",
      });
    },
  });

  // Delete managed value
  const deleteManagedValue = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetch(`/api/filter-options/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete value');
    },
    onSuccess: () => {
      toast({
        title: "Value deleted",
        description: "The managed value has been removed",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/filter-options'] });
    },
  });

  // Toggle managed value active status
  const toggleManagedValue = useMutation({
    mutationFn: async ({ id, isActive }: { id: number; isActive: boolean }) => {
      const response = await fetch(`/api/filter-options/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive }),
      });
      if (!response.ok) throw new Error('Failed to update value');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/filter-options'] });
    },
  });

  const filteredDynamicValues = dynamicValues.filter((v: DynamicValue) =>
    v.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredManagedValues = managedValues.filter((v: ManagedValue) =>
    v.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const managedValueSet = new Set(managedValues.map((v: ManagedValue) => v.value));
  const newDynamicValues = filteredDynamicValues.filter((v: DynamicValue) => 
    !managedValueSet.has(v.value)
  );

  const getCategoryIcon = () => {
    switch (selectedCategory) {
      case 'musicGenres':
        return <Music className="h-5 w-5" />;
      case 'fanEthnicity':
        return <Users className="h-5 w-5" />;
      case 'fanAttributes':
        return <Heart className="h-5 w-5" />;
      default:
        return null;
    }
  };

  const getCategoryLabel = () => {
    switch (selectedCategory) {
      case 'musicGenres':
        return 'Music Genres';
      case 'fanEthnicity':
        return 'Ethnicities';
      case 'fanAttributes':
        return 'Lifestyle Attributes';
      default:
        return selectedCategory;
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8 pt-24">
        <AdminHeader 
          title="Dynamic Values Management"
          description="Review and manage dynamic values extracted from your artist database"
        />

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Filter Options
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="category">Category</Label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger id="category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="musicGenres">Music Genres</SelectItem>
                    <SelectItem value="fanEthnicity">Ethnicities</SelectItem>
                    <SelectItem value="fanAttributes">Lifestyle Attributes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="search">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
                  <Input
                    id="search"
                    placeholder="Search values..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={() => refetchDynamic()}
                  className="w-full"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh Data
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="review" className="space-y-4">
          <TabsList>
            <TabsTrigger value="review">
              Review New Values ({newDynamicValues.length})
            </TabsTrigger>
            <TabsTrigger value="managed">
              Managed Values ({filteredManagedValues.length})
            </TabsTrigger>
            <TabsTrigger value="all">
              All Dynamic Values ({filteredDynamicValues.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="review">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {getCategoryIcon()}
                  New {getCategoryLabel()} from Database
                </CardTitle>
                <CardDescription>
                  These values were found in your artist database but haven't been reviewed yet.
                  Select values to approve them for use in filters.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {newDynamicValues.length > 0 ? (
                  <>
                    <div className="mb-4 flex justify-between items-center">
                      <div className="text-sm text-neutral-600">
                        {selectedValues.size} of {newDynamicValues.length} selected
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedValues(new Set(newDynamicValues.map((v: any) => v.value)))}
                        >
                          Select All
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedValues(new Set())}
                        >
                          Clear Selection
                        </Button>
                        <Button
                          size="sm"
                          disabled={selectedValues.size === 0}
                          onClick={() => createManagedValue.mutate(Array.from(selectedValues))}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Approve Selected
                        </Button>
                      </div>
                    </div>
                    
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12">
                              <Checkbox
                                checked={selectedValues.size === newDynamicValues.length && newDynamicValues.length > 0}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedValues(new Set(newDynamicValues.map((v: any) => v.value)));
                                  } else {
                                    setSelectedValues(new Set());
                                  }
                                }}
                              />
                            </TableHead>
                            <TableHead>Value</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {newDynamicValues.map((value: any) => (
                            <TableRow key={value.value}>
                              <TableCell>
                                <Checkbox
                                  checked={selectedValues.has(value.value)}
                                  onCheckedChange={(checked) => {
                                    const newSet = new Set(selectedValues);
                                    if (checked) {
                                      newSet.add(value.value);
                                    } else {
                                      newSet.delete(value.value);
                                    }
                                    setSelectedValues(newSet);
                                  }}
                                />
                              </TableCell>
                              <TableCell className="font-medium">{value.label}</TableCell>
                              <TableCell>
                                <Badge variant="secondary">New</Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-neutral-500">
                    No new values found. All dynamic values have been reviewed.
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="managed">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {getCategoryIcon()}
                  Managed {getCategoryLabel()}
                </CardTitle>
                <CardDescription>
                  These values have been approved and are available for use in filters.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {filteredManagedValues.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Value</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredManagedValues.map((value: any) => (
                          <TableRow key={value.id}>
                            <TableCell className="font-medium">{value.label}</TableCell>
                            <TableCell>
                              <Badge variant={value.isActive ? "default" : "secondary"}>
                                {value.isActive ? 'Active' : 'Inactive'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => toggleManagedValue.mutate({
                                    id: value.id,
                                    isActive: !value.isActive,
                                  })}
                                >
                                  {value.isActive ? 'Disable' : 'Enable'}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => deleteManagedValue.mutate(value.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-neutral-500">
                    No managed values found for this category.
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="all">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {getCategoryIcon()}
                  All {getCategoryLabel()} in Database
                </CardTitle>
                <CardDescription>
                  Complete list of all unique values found in your artist database.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {filteredDynamicValues.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Value</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredDynamicValues.map((value: any) => (
                          <TableRow key={value.value}>
                            <TableCell className="font-medium">{value.label}</TableCell>
                            <TableCell>
                              <Badge variant={managedValueSet.has(value.value) ? "default" : "secondary"}>
                                {managedValueSet.has(value.value) ? 'Managed' : 'Unreviewed'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-neutral-500">
                    No values found in the database.
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}